import { useEffect, useState } from 'react'
import api from '@/shared/api/client'
import type { ChartBucketKey } from '@/features/workspace/utils/status-map'

// ── Types ──

export type SearchTab = 'all' | 'icd' | 'cpt' | 'hcpcs' | 'modifier' | 'facility' | 'provider' | 'specialty' | 'encounter'

interface SearchResultItem {
  id: string
  title: string
  subtitle: string | null
  extra: string | null
  metadata?: Record<string, string | null>
  /** Code categories only. False when CMS retired the code and no current version
   *  exists — still returned so it stays findable, badged "No longer active". */
  /** Code categories only: 'active' | 'inactive' | 'upcoming', judged against today. */
  status?: 'active' | 'inactive' | 'upcoming'
  /** Code categories only: the row's window ('YYYY-MM-DD'), to say WHEN status changes. */
  effective_from?: string
  effective_to?: string | null
  /** Code categories only: a newer version takes effect on effective_to. */
  has_upcoming?: boolean
}

interface CategoryResult {
  /** Rows returned — not a total. */
  count: number
  /** More rows matched beyond the requested limit. */
  has_more?: boolean
  data: SearchResultItem[]
}

export type CategoryData = {
  icd: CategoryResult
  cpt: CategoryResult
  hcpcs: CategoryResult
  modifier: CategoryResult
  facility: CategoryResult
  provider: CategoryResult
  specialty: CategoryResult
  encounter: CategoryResult
}

interface GlobalSearchApiResponse {
  data: CategoryData
}

export interface GlobalSearchResult {
  id: string
  category: SearchTab
  title: string
  subtitle: string
  extra?: string
  metadata?: Record<string, string | null>
  /** Code categories only: 'active' | 'inactive' | 'upcoming', judged against today. */
  status?: 'active' | 'inactive' | 'upcoming'
  /** Code categories only: the row's window ('YYYY-MM-DD'), to say WHEN status changes. */
  effective_from?: string
  effective_to?: string | null
  /** Code categories only: a newer version takes effect on effective_to. */
  has_upcoming?: boolean
}

// ── Config ──

export const CATEGORY_KEYS: readonly (keyof CategoryData)[] = ['icd', 'cpt', 'hcpcs', 'modifier', 'facility', 'provider', 'specialty', 'encounter']

// ── API ──

/** Rows per category on the first request. */
export const SEARCH_LIMIT = 12
/** Backend max (search.routes.ts) — what a panel tab's "Show more" re-fetches. */
export const SEARCH_MAX_LIMIT = 50

/**
 * Chart filters for the quick search's chart category and Advanced Search
 * (facility ids, Chart Queue / History tab keys). They only narrow the chart
 * rows — same matcher, same ranking.
 */
export interface ChartFilters {
  facility_ids?: string[]
  buckets?: ChartBucketKey[]
}

export function fetchGlobalSearch(
  q: string,
  limit = SEARCH_LIMIT,
  tab?: keyof CategoryData,
  signal?: AbortSignal,
  filters?: ChartFilters | null,
) {
  const params: Record<string, string | number> = tab ? { q, limit, tab } : { q, limit }
  // Comma-separated; ids and tab keys only — never an identifier.
  if (filters?.facility_ids?.length) params.facility_ids = filters.facility_ids.join(',')
  if (filters?.buckets?.length) params.buckets = filters.buckets.join(',')
  return api
    .get<GlobalSearchApiResponse>('/search/global', { params, signal })
    .then((r) => r.data.data)
}

// ── Flatten ──

export function flattenResults(data: CategoryData, activeTab: SearchTab): GlobalSearchResult[] {
  const categories = activeTab === 'all' ? CATEGORY_KEYS : [activeTab as keyof CategoryData]
  const results: GlobalSearchResult[] = []
  for (const cat of categories) {
    const catData = data[cat]
    if (!catData) continue
    for (const item of catData.data) {
      results.push({
        id: `${cat}-${item.id}`,
        category: cat as SearchTab,
        title: item.title ?? '',
        subtitle: item.subtitle ?? '',
        extra: item.extra ?? undefined,
        metadata: item.metadata,
        status: item.status ?? undefined,
        effective_from: item.effective_from ?? undefined,
        effective_to: item.effective_to ?? undefined,
        has_upcoming: item.has_upcoming ?? undefined,
      })
    }
  }
  return results
}

// ── Hook ──

/** `value`, once it has stopped changing for `ms` (the panel's 150 ms keystroke debounce). */
export function useDebouncedValue<T>(value: T, ms: number): T {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return v
}

/** A per-category query key; chart filters (when set) are part of it. */
export const categoryKey = (q: string, cat: keyof CategoryData, limit: number, filters?: ChartFilters | null) =>
  filters ? ['global-search', q, cat, limit, filters] : ['global-search', q, cat, limit]
